import { useState } from "react";
import { ArrowUpRight, Maximize2 } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { IMG, SITE } from "@/data/site";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Kicker, Reveal } from "./Reveal";
import { InstagramIcon } from "./Icons";

const ITEMS = [
  { src: IMG.mural, span: "md:col-span-7 md:row-span-2", rot: "-rotate-[1.2deg]", tape: ["tape-tl"], pos: "object-center" },
  { src: IMG.athlete, span: "row-span-2 md:col-span-5 md:row-span-4", rot: "rotate-[1.5deg]", tape: ["tape-tr"], pos: "object-[50%_30%]" },
  { src: IMG.badge, span: "md:col-span-3 md:row-span-2", rot: "rotate-[2deg]", tape: [], pos: "object-center" },
  { src: IMG.interior, span: "md:col-span-4 md:row-span-2", rot: "-rotate-[1.8deg]", tape: ["tape-br"], pos: "object-center" },
  { src: IMG.crew, span: "md:col-span-8 md:row-span-2", rot: "rotate-[0.8deg]", tape: ["tape-tl", "tape-tr"], pos: "object-center" },
];

export const Gallery = () => {
  const { t } = useLang();
  const [active, setActive] = useState(null);

  return (
    <section id="galleria" data-testid="gallery-section" className="relative paper-concrete text-ink overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-24 sm:pt-32 sm:pb-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <Reveal>
            <Kicker testid="gallery-kicker">{t.gallery.kicker}</Kicker>
            <h2 data-testid="gallery-title" className="font-display text-6xl sm:text-7xl lg:text-8xl mt-5 leading-[0.85]">
              {t.gallery.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="md:text-right">
            <p className="max-w-xs text-ink/70 md:ml-auto">{t.gallery.sub}</p>
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink/50 mt-2 flex items-center gap-2 md:justify-end">
              <Maximize2 className="w-3.5 h-3.5" /> {t.gallery.hint}
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[260px] md:auto-rows-[150px] lg:auto-rows-[165px] gap-8 md:gap-7">
          {ITEMS.map((it, i) => (
            <Reveal key={i} delay={i * 0.05} className={`${it.span}`}>
              <button
                type="button"
                data-testid={`gallery-item-${i}`}
                onClick={() => setActive(i)}
                className="gcard block w-full h-full text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-hammer-red"
                aria-label={t.gallery.captions[i]}
              >
                {it.tape.map((tp) => (
                  <span key={tp} className={`tape ${tp}`} />
                ))}
                <div className={`gframe ${it.rot}`}>
                  <img src={it.src} alt={t.gallery.captions[i]} loading="lazy" className={`w-full h-full object-cover ${it.pos} photo-warm`} />
                  <div className="tint-red" />
                  <div className="grain-layer" />
                  <span className="absolute top-3 right-3 z-[3] font-mono text-[10px] tracking-[0.2em] bg-ink text-bone px-2 py-1">
                    FIG. 0{i + 1}
                  </span>
                  <span className="absolute bottom-3 left-3 z-[3] font-hand text-lg sm:text-xl bg-bone text-ink px-3 py-1 -rotate-2 border-2 border-ink">
                    {t.gallery.captions[i]}
                  </span>
                </div>
              </button>
            </Reveal>
          ))}

          {/* Instagram tile */}
          <Reveal delay={0.25} className="md:col-span-4 md:row-span-2">
            <a
              href={SITE.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="gallery-instagram-link"
              className="group relative flex flex-col justify-between h-full bg-ink text-bone border-[3px] border-ink p-6 shadow-[8px_8px_0_#D7192A] hover:shadow-[12px_12px_0_#D7192A] transition-shadow overflow-hidden"
            >
              <div className="grain-layer" />
              <div className="relative z-10 flex items-center justify-between">
                <InstagramIcon className="w-9 h-9 text-hammer-red" />
                <ArrowUpRight className="w-7 h-7 group-hover:rotate-45 transition-transform duration-300" />
              </div>
              <div className="relative z-10">
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-bone/60">{t.gallery.igTitle}</p>
                <p className="font-display text-4xl sm:text-5xl leading-none mt-2 group-hover:text-hammer-red transition-colors">
                  {SITE.instagramHandle}
                </p>
                <p className="text-sm text-bone/70 mt-3">{t.gallery.igText}</p>
                <p className="mt-4 font-display text-xl tracking-wider link-slide w-max">{t.gallery.igCta}</p>
              </div>
            </a>
          </Reveal>
        </div>
      </div>

      <Dialog open={active !== null} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="gallery-lightbox"
          className="max-w-5xl w-[94vw] p-0 bg-ink border-4 border-bone rounded-none overflow-hidden [&>button]:text-bone [&>button]:bg-hammer-red [&>button]:rounded-none [&>button]:p-1 [&>button]:opacity-100"
        >
          <DialogTitle className="sr-only">{active !== null ? t.gallery.captions[active] : ""}</DialogTitle>
          <DialogDescription className="sr-only">Hammer Gym Lugano</DialogDescription>
          {active !== null && (
            <figure>
              <img src={ITEMS[active].src} alt={t.gallery.captions[active]} className="w-full max-h-[78vh] object-contain bg-ink" />
              <figcaption className="flex items-center justify-between px-4 py-3 text-bone">
                <span className="font-hand text-xl">{t.gallery.captions[active]}</span>
                <span className="font-mono text-[11px] tracking-[0.2em] text-bone/60">FIG. 0{active + 1} / 05</span>
              </figcaption>
            </figure>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
